import {
  entryToDatabase,
  fieldToDatabase,
  viewsByIdToDatabase
} from '@databases/collab/convert/to-database';
import { type DatabaseDiff, type YDatabase } from '@databases/collab/y-database';
import { type CommentThreadJSON } from '@databases/model/commentThreads';
import { type DatabaseViewJSON } from '@databases/model/views/view';
import { type SerializedError } from '@databases/types';
import {
  type BasicDatabase,
  type BrokenDatabase,
  DEFAULT_ENTRY_ID,
  type Database,
  type ShallowDatabase
} from '@databases/types/database';

import {
  isBasicDatabase,
  isNonBasicDatabase,
  isNonShallowDatabase,
  isShallowDatabase
} from '../../database/predicates.ts';
import { type PayloadAction, createSlice } from '../../shims/redux-toolkit.ts';
import { type AsyncOperation, AsyncOperationStatus } from '../async-operation.ts';

export interface DatabasesState {
  databases: Record<string, Database | ShallowDatabase | BasicDatabase | BrokenDatabase>;

  spaceDatabasesStatus: Record<string, AsyncOperation<void>>;
  databaseByContentIdStatus: Record<string, AsyncOperation<void>>;
  /** Load status, keyed by `${spaceKey}:${database.id}` */
  databaseByIdStatus: Record<string, AsyncOperation<void>>;
}

const initialState: DatabasesState = {
  databases: {},

  spaceDatabasesStatus: {},
  databaseByContentIdStatus: {},
  databaseByIdStatus: {}
};

const databasesSlice = createSlice({
  name: 'databases',
  initialState,
  reducers: {
    loadSpaceDatabasesPending: (state, action: PayloadAction<{ spaceKey: string }>) => {
      const { spaceKey } = action.payload;

      state.spaceDatabasesStatus[spaceKey] = { status: AsyncOperationStatus.PENDING };
    },

    loadSpaceDatabasesFinished: (state, action: PayloadAction<{ spaceKey: string }>) => {
      const { spaceKey } = action.payload;

      state.spaceDatabasesStatus[spaceKey] = { status: AsyncOperationStatus.SUCCEEDED };
    },

    loadSpaceDatabasesError: (
      state,
      action: PayloadAction<{ spaceKey: string; error: SerializedError }>
    ) => {
      const { spaceKey, error } = action.payload;

      state.spaceDatabasesStatus[spaceKey] = {
        status: AsyncOperationStatus.ERROR,
        error
      };
    },

    loadDatabaseByContentIdPending: (
      state,
      action: PayloadAction<{ contentId: string }>
    ) => {
      const { contentId } = action.payload;

      state.databaseByContentIdStatus[contentId] = {
        status: AsyncOperationStatus.PENDING
      };
    },

    loadDatabaseByContentIdFinished: (
      state,
      action: PayloadAction<{ contentId: string }>
    ) => {
      const { contentId } = action.payload;

      state.databaseByContentIdStatus[contentId] = {
        status: AsyncOperationStatus.SUCCEEDED
      };
    },

    loadDatabaseByContentIdError: (
      state,
      action: PayloadAction<{ contentId: string; error: SerializedError }>
    ) => {
      const { contentId, error } = action.payload;

      state.databaseByContentIdStatus[contentId] = {
        status: AsyncOperationStatus.ERROR,
        error
      };
    },

    addDatabasesToList: (
      state,
      action: PayloadAction<{
        databases: Array<Database | ShallowDatabase | BasicDatabase | BrokenDatabase>;
      }>
    ) => {
      const { databases } = action.payload;

      for (const database of databases) {
        // Skip adding basic databases of which a non-basic version is already in the store
        if (
          isBasicDatabase(database) &&
          state.databases[database.contentId] &&
          !isBasicDatabase(state.databases[database.contentId])
        ) {
          continue;
        }

        // Skip adding shallow databases of which a non-shallow version is already in the store
        if (
          isShallowDatabase(database) &&
          state.databases[database.contentId] &&
          !isShallowDatabase(state.databases[database.contentId])
        ) {
          continue;
        }

        state.databases[database.contentId] = database;
      }
    },

    applyDatabaseDiff: (
      state,
      action: PayloadAction<{
        contentId: string;
        collabDatabase: YDatabase;
        diff: DatabaseDiff;
        isReadOnly: boolean;
      }>
    ) => {
      const { contentId, collabDatabase, diff, isReadOnly } = action.payload;
      const database = state.databases[contentId];

      if (!database) {
        return;
      }
      if (!isNonBasicDatabase(database)) {
        throw new Error('Invalid database type');
      }
      if (!isNonShallowDatabase(database)) {
        return;
      }

      let entriesArrayNeedsUpdate = false;

      for (const changedEntryId of diff.changedEntryIds) {
        if (collabDatabase.entriesById[changedEntryId]) {
          database.entriesById[changedEntryId] = entryToDatabase(
            changedEntryId,
            collabDatabase.entriesById[changedEntryId].toJSON()
          );
          if (!database.entries.includes(changedEntryId)) {
            entriesArrayNeedsUpdate = true;
          }
        } else {
          database.entries = database.entries.filter(
            (entryId) => entryId !== changedEntryId
          );
          delete database.entriesById[changedEntryId];
        }
      }

      if (diff.threadsChanged) {
        database.commentThreads = Object.keys(collabDatabase.commentThreads).reduce(
          (acc, key) => {
            acc[key] = collabDatabase.commentThreads[key].toJSON();
            return acc;
          },
          {} as Record<string, CommentThreadJSON[]>
        );
      }

      let fieldsArrayNeedsUpdate = false;

      for (const changedFieldId of diff.changedFieldIds) {
        if (collabDatabase.fieldsById[changedFieldId]) {
          database.fieldsById[changedFieldId] = fieldToDatabase(
            changedFieldId,
            collabDatabase.fieldsById[changedFieldId].toJSON()
          );
          if (!database.fields.includes(changedFieldId)) {
            fieldsArrayNeedsUpdate = true;
          }
        } else {
          database.fields = database.fields.filter(
            (fieldId) => fieldId !== changedFieldId
          );
          delete database.fieldsById[changedFieldId];
        }
      }

      if (diff.defaultEntryChanged) {
        database.defaultEntry = entryToDatabase(
          DEFAULT_ENTRY_ID,
          collabDatabase.defaultEntry.toJSON()
        );
      }

      // Update views if changed OR if there's a mismatch (defensive check for sync issues)
      const viewsNeedUpdate =
        diff.viewsChanged ||
        (collabDatabase.views.length > 0 && database.views.length === 0);

      if (viewsNeedUpdate) {
        database.views = viewsByIdToDatabase(
          Object.keys(collabDatabase.viewsById).reduce(
            (acc, key) => {
              acc[key] = collabDatabase.viewsById[key].toJSON();
              return acc;
            },
            {} as Record<string, DatabaseViewJSON>
          ),
          collabDatabase.views
        );
      }

      if (diff.othersChanged) {
        database.defaultViewId = collabDatabase.defaultViewId;
        database.meta.isLocked = collabDatabase.isLocked;
      }

      if (diff.entriesOrderingChanged || entriesArrayNeedsUpdate) {
        database.entries = collabDatabase.entries;
      }

      if (diff.fieldsOrderingChanged || fieldsArrayNeedsUpdate) {
        database.fields = collabDatabase.fields;
      }

      // Readonly logic
      if (database.operations.updateEntries !== !isReadOnly) {
        database.operations.updateEntries = !isReadOnly;
      }

      if (database.operations.updateMetadata !== !isReadOnly) {
        database.operations.updateMetadata = !isReadOnly;
      }

      if (database.operations.delete !== !isReadOnly) {
        database.operations.delete = !isReadOnly;
      }
    },

    mergeDatabaseJSON: (
      state,
      action: PayloadAction<{ contentId: string; database: Partial<Database> }>
    ) => {
      const { contentId, database } = action.payload;

      state.databases[contentId] = {
        ...database,
        ...state.databases[contentId]
      };
    },

    deleteDatabasesFromList: (state, action: PayloadAction<{ contentIds: string[] }>) => {
      const { contentIds } = action.payload;

      for (const contentId of contentIds) {
        delete state.databases[contentId];
      }
    }
  }
});

export default databasesSlice;

export function getIdKey(spaceKey: string, id: string): string {
  return `${spaceKey}:${id}`;
}
